import { WebsiteProviderSchema } from '../../Scraping.js';
import { z } from 'zod';
import { TYPE_OF_ORDER_ENUM } from '../../../constants.js';
import { inventoryDataSourceSchema, InventoryDataSource } from '../../inventory/InventoryItem.js';
import { serviceConfigSchema } from './SharedServiceConfig.js';

// ============================================================================
// Enums
// ============================================================================
const DMSProvider = {
    LIGHTSPEED: 'lightspeed',
    OTHER: 'other',
};
const dmsProviderValues = [DMSProvider.LIGHTSPEED, DMSProvider.OTHER];
const dmsProviderSchema = z.enum(dmsProviderValues);
// Field group enum
const InventoryFieldGroup = {
    VEHICLE_IDENTITY: 'VEHICLE_IDENTITY',
    METADATA: 'METADATA',
    SPECS: 'SPECS',
    FEATURES: 'FEATURES',
    DESCRIPTION: 'DESCRIPTION',
    MSRP: 'MSRP',
    RETAIL_PRICE: 'RETAIL_PRICE',
    ADVERTISED_PRICE: 'ADVERTISED_PRICE',
    CONDITION: 'CONDITION',
    IMAGES: 'IMAGES',
};
const inventoryFieldGroupValues = [
    InventoryFieldGroup.VEHICLE_IDENTITY,
    InventoryFieldGroup.METADATA,
    InventoryFieldGroup.SPECS,
    InventoryFieldGroup.FEATURES,
    InventoryFieldGroup.DESCRIPTION,
    InventoryFieldGroup.MSRP,
    InventoryFieldGroup.RETAIL_PRICE,
    InventoryFieldGroup.ADVERTISED_PRICE,
    InventoryFieldGroup.CONDITION,
    InventoryFieldGroup.IMAGES,
];
const inventoryFieldGroupSchema = z.enum(inventoryFieldGroupValues);
// Order Type Enum
const ORDER_TYPE_ENUM = {
    PREORDER: 'PREORDER',
    BUILD_TO_ORDER: 'BUILD_TO_ORDER',
    INVENTORY_ORDER: 'INVENTORY_ORDER',
};
const orderTypeValues = [
    ORDER_TYPE_ENUM.PREORDER,
    ORDER_TYPE_ENUM.BUILD_TO_ORDER,
    ORDER_TYPE_ENUM.INVENTORY_ORDER,
];
const orderTypeSchema = z.enum(orderTypeValues);
// ============================================================================
// DMS Configuration Schemas
// ============================================================================
const lightspeedDmsConnectionConfigSchema = z.object({
    cmf: z.string(),
});
const otherDmsConnectionConfigSchema = z.object({
    name: z.string(),
});
const dmsConnectionConfigSchema = z.object({
    dmsProvider: dmsProviderSchema,
    connectionDetails: z.union([
        lightspeedDmsConnectionConfigSchema,
        otherDmsConnectionConfigSchema,
    ]),
    hasProvidedAuthConsent: z.boolean(),
    userDefinedFields: z
        .object({
        vehicleDetailsUrlFieldId: z.string(),
    })
        .optional(),
});
// ============================================================================
// Field Group Precedence
// ============================================================================
// Configuration structure for each field group
const fieldGroupConfigurationSchema = z.object({
    name: z.string(),
    description: z.string(),
    fields: z.array(z.string()),
    defaultSource: inventoryDataSourceSchema,
    availableSources: z.array(inventoryDataSourceSchema),
});
const fieldGroupPrecedenceSchema = z.record(inventoryFieldGroupSchema, inventoryDataSourceSchema);
// ============================================================================
// Scraping, CRM, and Admin Portal Config Schemas
// ============================================================================
const inventoryUrlEntrySchema = z.object({
    url: z.string(),
    paginationPattern: z.string(),
    label: z.string().optional(),
});
const servicesProviderScrapingConfigSchema = z.object({
    enabled: z.boolean(),
    allInventoryUrl: z.string(),
    allInventoryUrlPaginationPattern: z.string(),
    inventoryUrls: z.array(inventoryUrlEntrySchema).optional(),
    websiteProvider: WebsiteProviderSchema.optional(),
    inventoryCreationEnabled: z.boolean().optional(),
    inventoryArchiveEnabled: z.boolean().optional(),
    fuzzyMatch: z.boolean().optional(),
});
const crmConfigSchema = z.object({
    vendorName: z.string(),
});
const adminPortalConfigSchema = z.object({
    importInventoryEnabled: z.boolean().optional(),
});
// ============================================================================
// Core Schema
// ============================================================================
const inventoryServiceConfigSchema = serviceConfigSchema.extend({
    dmsConnectionConfig: dmsConnectionConfigSchema.optional(), // not everyone doing inventory has a DMS connection
    fieldGroupPrecedence: fieldGroupPrecedenceSchema,
    scrapingConfig: servicesProviderScrapingConfigSchema,
    crm: crmConfigSchema.optional(),
    adminPortalConfig: adminPortalConfigSchema.optional(),
    // The fields below are from CanonVehicle.config.sale (will be added during svp rollup)
    averageProductionTime: z.number(),
    typeOfOrder: z.enum([
        TYPE_OF_ORDER_ENUM.PREORDER,
        TYPE_OF_ORDER_ENUM.BUILD_TO_ORDER,
        TYPE_OF_ORDER_ENUM.INVENTORY_ORDER,
    ]),
    skipBuildingCanonVehicles: z.boolean().optional(),
    isLiveForOnlineSales: z.boolean().optional(),
    autoPublishInventory: z.boolean().optional(),
    autoMarkForOnlineSale: z.boolean().optional(),
    showLockedInventory: z.boolean().optional(),
});
// ============================================================================
// DTO Schema
// ============================================================================
const lightspeedDmsConnectionConfigDtoSchema = lightspeedDmsConnectionConfigSchema;
const otherDmsConnectionConfigDtoSchema = otherDmsConnectionConfigSchema;
const dmsConnectionConfigDtoSchema = dmsConnectionConfigSchema;
const fieldGroupPrecedenceDtoSchema = fieldGroupPrecedenceSchema;
const servicesProviderScrapingConfigDtoSchema = servicesProviderScrapingConfigSchema;
const crmConfigDtoSchema = crmConfigSchema;
const adminPortalConfigDtoSchema = adminPortalConfigSchema;
const inventoryServiceConfigDtoSchema = serviceConfigSchema.extend({
    dmsConnectionConfig: dmsConnectionConfigDtoSchema.optional(),
    fieldGroupPrecedence: fieldGroupPrecedenceDtoSchema.optional(),
    scrapingConfig: servicesProviderScrapingConfigDtoSchema.optional(),
    crm: crmConfigDtoSchema.optional(),
    adminPortalConfig: adminPortalConfigDtoSchema.optional(),
    averageProductionTime: z.coerce.number(),
    typeOfOrder: z.enum([
        TYPE_OF_ORDER_ENUM.PREORDER,
        TYPE_OF_ORDER_ENUM.BUILD_TO_ORDER,
        TYPE_OF_ORDER_ENUM.INVENTORY_ORDER,
    ]),
    skipBuildingCanonVehicles: z.boolean().optional(),
    isLiveForOnlineSales: z.boolean().optional(),
    autoPublishInventory: z.boolean().optional(),
    autoMarkForOnlineSale: z.boolean().optional(),
    showLockedInventory: z.boolean().optional(),
});
// ============================================================================
// General Inventory Config DTO Schema
// ============================================================================
const inventoryGeneralConfigDtoSchema = z.object({
    isLiveForOnlineSales: z.boolean().optional(),
    skipBuildingCanonVehicles: z.boolean().optional(),
    autoPublishInventory: z.boolean().optional(),
    autoMarkForOnlineSale: z.boolean().optional(),
    showLockedInventory: z.boolean().optional(),
});
// ======================================================================================
// Util - getFieldGroupForField
// ======================================================================================
// Constant defining allowed configurations (for UI settings)
const INVENTORY_FIELD_GROUP_CONFIGS = {
    [InventoryFieldGroup.VEHICLE_IDENTITY]: {
        name: 'Vehicle Identity',
        description: 'Make, model, and other vehicle identifiers',
        fields: ['make', 'model', 'fullName'],
        defaultSource: InventoryDataSource.OEM,
        availableSources: [
            InventoryDataSource.OEM,
            InventoryDataSource.DMS,
            InventoryDataSource.WEBSITE,
        ],
    },
    [InventoryFieldGroup.METADATA]: {
        name: 'Metadata',
        description: 'VIN, odometer reading, stock number, stock number, color, etc.',
        fields: [
            'vin',
            'modelYear',
            'odometerReading',
            'odometerUnit',
            'engineNumber',
            'color',
            'baseColor',
            'stockNumber',
            'type',
        ],
        defaultSource: InventoryDataSource.DMS,
        availableSources: [InventoryDataSource.DMS, InventoryDataSource.WEBSITE],
    },
    [InventoryFieldGroup.SPECS]: {
        name: 'Specs',
        description: 'Engine, transmission, weight, seat height, fuel capacity',
        fields: [
            'specs.areTireRimSizes19OrLess',
            'specs.bodyType',
            'specs.certifiedEmissionsVehicle',
            'specs.cylinders',
            'specs.dimensions',
            'specs.dotVehicle',
            'specs.driveTrain',
            'specs.engineSize',
            'specs.fuelType',
            'specs.grossWeight',
            'specs.grossWeightRating',
            'specs.horsePower',
            'specs.milePerGalone',
            'specs.motoCubicCentimeters',
            'specs.numberOfWheels',
            'specs.seatingCapacity',
            'specs.transmission',
            'specs.trimLevel',
            'specs.unladenWeight',
            'specs.vehicleType',
            'specs.seatHeight',
            'specs.weight',
            'specs.horsepower',
            'specs.engine.fuelCapacity',
            'specs.engine.engineDisplacement',
            'specs.battery.capacity',
            'specs.battery.range',
            'specs.battery.power',
        ],
        defaultSource: InventoryDataSource.OEM,
        availableSources: [InventoryDataSource.OEM, InventoryDataSource.WEBSITE],
    },
    [InventoryFieldGroup.FEATURES]: {
        name: 'Features',
        description: 'Performance, technology, safety, exterior & design',
        fields: ['features'],
        defaultSource: InventoryDataSource.WEBSITE,
        availableSources: [InventoryDataSource.WEBSITE],
    },
    [InventoryFieldGroup.DESCRIPTION]: {
        name: 'Description',
        description: 'Vehicle description',
        fields: ['description'],
        defaultSource: InventoryDataSource.WEBSITE,
        availableSources: [InventoryDataSource.WEBSITE, InventoryDataSource.OEM],
    },
    [InventoryFieldGroup.MSRP]: {
        name: 'MSRP',
        description: 'Manufacturer suggested retail price',
        fields: ['pricing.msrp'],
        defaultSource: InventoryDataSource.OEM,
        availableSources: [InventoryDataSource.OEM],
    },
    [InventoryFieldGroup.RETAIL_PRICE]: {
        name: 'Retail price',
        description: 'The true selling price that customers pay after inquiry',
        fields: ['pricing.retailPrice', 'pricing.dealerRetailPrice'],
        defaultSource: InventoryDataSource.DMS,
        availableSources: [
            InventoryDataSource.EKHO,
            InventoryDataSource.DMS,
            InventoryDataSource.WEBSITE,
        ],
    },
    [InventoryFieldGroup.ADVERTISED_PRICE]: {
        name: 'Advertised price',
        description: 'The price displayed to buyers on the website',
        fields: ['pricing.advertisedPrice'],
        defaultSource: InventoryDataSource.WEBSITE,
        availableSources: [
            InventoryDataSource.EKHO,
            InventoryDataSource.DMS,
            InventoryDataSource.WEBSITE,
        ],
    },
    [InventoryFieldGroup.CONDITION]: {
        name: 'Condition',
        description: 'New or used condition of the vehicle',
        fields: ['newUsed'],
        defaultSource: InventoryDataSource.DMS,
        availableSources: [
            InventoryDataSource.EKHO,
            InventoryDataSource.DMS,
            InventoryDataSource.WEBSITE,
        ],
    },
    [InventoryFieldGroup.IMAGES]: {
        name: 'Images',
        description: 'Images of the vehicle',
        fields: ['imageUrls'],
        defaultSource: InventoryDataSource.WEBSITE,
        availableSources: [InventoryDataSource.WEBSITE],
    },
};
/**
 * Reverse lookup map: Field name → Field group
 * Generated from INVENTORY_FIELD_GROUP_CONFIGS to maintain single source of truth
 * This map is built at module load time and provides O(1) lookup performance
 */
const FIELD_TO_FIELD_GROUP_MAP = (() => {
    const map = new Map();
    Object.entries(INVENTORY_FIELD_GROUP_CONFIGS).forEach(([fieldGroup, config]) => {
        config.fields.forEach((field) => {
            if (map.has(field)) {
                throw new Error(`Field "${field}" is defined in multiple field groups: ${map.get(field)} and ${fieldGroup}`);
            }
            map.set(field, fieldGroup);
        });
    });
    return map;
})();
/**
 * Helper function to get field group for a given field name
 * @param fieldName - The inventory field name (e.g., 'advertisedPrice', 'description')
 * @returns The field group enum value, or undefined if field is not configured
 */
function getFieldGroupForField(fieldName) {
    return FIELD_TO_FIELD_GROUP_MAP.get(fieldName);
}

export { DMSProvider, FIELD_TO_FIELD_GROUP_MAP, INVENTORY_FIELD_GROUP_CONFIGS, InventoryFieldGroup, ORDER_TYPE_ENUM, adminPortalConfigDtoSchema, adminPortalConfigSchema, crmConfigDtoSchema, crmConfigSchema, dmsConnectionConfigDtoSchema, dmsConnectionConfigSchema, dmsProviderSchema, dmsProviderValues, fieldGroupConfigurationSchema, fieldGroupPrecedenceDtoSchema, fieldGroupPrecedenceSchema, getFieldGroupForField, inventoryFieldGroupSchema, inventoryFieldGroupValues, inventoryGeneralConfigDtoSchema, inventoryServiceConfigDtoSchema, inventoryServiceConfigSchema, inventoryUrlEntrySchema, lightspeedDmsConnectionConfigDtoSchema, lightspeedDmsConnectionConfigSchema, orderTypeSchema, orderTypeValues, otherDmsConnectionConfigDtoSchema, otherDmsConnectionConfigSchema, servicesProviderScrapingConfigDtoSchema, servicesProviderScrapingConfigSchema };
//# sourceMappingURL=InventoryServiceConfig.js.map
